import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import { useTheme } from 'styled-components';

interface CustomInputDateProps {
  name: string;
  title: string;
  value?: string; // Formato 'yyyy-mm-dd'
  readOnly?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  required?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  minDate?: Date;
  maxDate?: Date;
  'data-testid'?: boolean | string;
}

export interface CustomInputDateRef {
  triggerBlur: () => string;
}

const CustomInputDate = forwardRef<CustomInputDateRef, CustomInputDateProps>(
  (
    {
      name,
      title,
      value = '',
      readOnly = false,
      onChange,
      required = false,
      textAlign = 'left',
      minDate,
      maxDate,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();

    const controlId = name;

    const testId =
      typeof dataTestId === 'string'
        ? dataTestId
        : dataTestId === true
          ? `input-date-${controlId}`
          : undefined;

    useImperativeHandle(ref, () => ({
      triggerBlur: () => {
        return handleBlur({
          target: inputRef.current,
        } as React.FocusEvent<HTMLInputElement>);
      },
    }));

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setInternalValue(value);
      if (onChange) {
        onChange(event);
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>): string => {
      const { value } = event.target;
      let finalValue = value;

      if (value.length === 10) {
        const newDate = new Date(value + 'T00:00:00');

        if (minDate) {
          const min = new Date(
            minDate.valueOf() + minDate.getTimezoneOffset() * 60 * 1000,
          );
          min.setHours(0, 0, 0, 0);
          if (newDate < min) {
            finalValue = formatDateForInput(min) || '';
          }
        }
        if (maxDate) {
          const max = new Date(
            maxDate.valueOf() + maxDate.getTimezoneOffset() * 60 * 1000,
          );
          max.setHours(0, 0, 0, 0);
          if (newDate > max) {
            finalValue = formatDateForInput(max) || '';
          }
        }
      }

      setInternalValue(finalValue);
      if (onChange && finalValue !== value) {
        const newEvent = {
          ...event,
          target: { ...event.target, value: finalValue },
        };
        onChange(newEvent as React.ChangeEvent<HTMLInputElement>);
      }
      return finalValue;
    };

    const formatDateForInput = (date: Date | undefined) => {
      if (!date) return undefined;
      return date.toISOString().split('T')[0];
    };

    return (
      <FormGroup controlId={name}>
        <FormLabel
          style={{ color: theme.colors.botaoConfirmar }}
          data-testid={testId ? `${testId}-label` : undefined}
        >
          {required ? (
            <>
              <span
                style={{
                  color: theme.colors.botaoConfirmar,
                  fontWeight: 'bold',
                }}
              >
                {title}
              </span>
              <span
                style={{ color: theme.colors.vermelho, fontWeight: 'bold' }}
              >
                *
              </span>
            </>
          ) : (
            title
          )}
        </FormLabel>
        <FormControl
          type="date"
          name={name}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          readOnly={readOnly}
          required={required}
          min={formatDateForInput(minDate)}
          max={formatDateForInput(maxDate)}
          ref={inputRef}
          data-testid={testId}
          style={{
            textAlign: textAlign,
            backgroundColor: readOnly ? '#e9ecef' : theme.colors.white,
          }}
        />
      </FormGroup>
    );
  },
);

export default CustomInputDate;
